import { AsYouType, parsePhoneNumberFromString, } from 'libphonenumber-js';
export const PHONE_COUNTRY_OPTIONS = [
    { iso2: 'BE', value: 'BE', label: 'Belgique', flag: '🇧🇪', callingCode: '+32' },
    { iso2: 'FR', value: 'FR', label: 'France', flag: '🇫🇷', callingCode: '+33' },
    { iso2: 'RE', value: 'RE', label: 'La Réunion', flag: '🇷🇪', callingCode: '+262' },
    { iso2: 'LU', value: 'LU', label: 'Luxembourg', flag: '🇱🇺', callingCode: '+352' },
    { iso2: 'NL', value: 'NL', label: 'Pays-Bas', flag: '🇳🇱', callingCode: '+31' },
    { iso2: 'DE', value: 'DE', label: 'Allemagne', flag: '🇩🇪', callingCode: '+49' },
    { iso2: 'GB', value: 'GB', label: 'Royaume-Uni', flag: '🇬🇧', callingCode: '+44' },
    { iso2: 'US', value: 'US', label: 'États-Unis', flag: '🇺🇸', callingCode: '+1' },
];
const SUPPORTED_COUNTRIES = new Set(PHONE_COUNTRY_OPTIONS.map((item) => item.iso2));
const COUNTRY_NAME_TO_ISO = {
    be: 'BE',
    belgium: 'BE',
    belgique: 'BE',
    fr: 'FR',
    france: 'FR',
    re: 'RE',
    reunion: 'RE',
    'la reunion': 'RE',
    'ile de la reunion': 'RE',
    'ile reunion': 'RE',
    lu: 'LU',
    luxembourg: 'LU',
    nl: 'NL',
    netherlands: 'NL',
    holland: 'NL',
    'pays-bas': 'NL',
    'pays bas': 'NL',
    de: 'DE',
    germany: 'DE',
    deutschland: 'DE',
    allemagne: 'DE',
    gb: 'GB',
    uk: 'GB',
    'united kingdom': 'GB',
    'royaume-uni': 'GB',
    'royaume uni': 'GB',
    us: 'US',
    usa: 'US',
    'united states': 'US',
    'etats-unis': 'US',
    canada: 'US',
};
function isSupportedPhoneCountry(country) {
    return Boolean(country && SUPPORTED_COUNTRIES.has(country));
}
/**
 * Supprime uniquement les caractères qui n'ont aucun sens
 * pour une saisie téléphonique.
 *
 * On laisse AsYouType gérer espaces, parenthèses, tirets, etc.
 */
function sanitizePhoneInput(value) {
    const trimmed = value.trimStart();
    // On conserve éventuellement le + initial et les chiffres.
    // AsYouType n'a pas besoin de la ponctuation saisie par l'utilisateur.
    const hasLeadingPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) {
        return hasLeadingPlus ? '+' : '';
    }
    return hasLeadingPlus ? `+${digits}` : digits;
}
export function parsePhoneInput(rawValue, selectedCountry) {
    const sanitized = sanitizePhoneInput(rawValue);
    if (!sanitized || sanitized === '+') {
        return {
            displayValue: sanitized,
            e164Value: null,
            country: selectedCountry,
            isValid: false,
            isPossible: false,
        };
    }
    const isInternational = sanitized.startsWith('+');
    /*
     * Pour un numéro international on NE force PAS le pays :
     * libphonenumber-js doit pouvoir le détecter grâce au préfixe.
     *
     * Pour un numéro national on utilise le pays sélectionné.
     */
    const formatter = isInternational
        ? new AsYouType()
        : new AsYouType(selectedCountry);
    const displayValue = formatter.input(sanitized);
    const parsed = isInternational
        ? parsePhoneNumberFromString(sanitized)
        : parsePhoneNumberFromString(sanitized, selectedCountry);
    let resolvedCountry = selectedCountry;
    if (isInternational &&
        parsed?.country &&
        isSupportedPhoneCountry(parsed.country)) {
        resolvedCountry = parsed.country;
    }
    const isValid = Boolean(parsed?.isValid());
    const isPossible = Boolean(parsed?.isPossible());
    return {
        displayValue,
        e164Value: isValid ? parsed.number : null,
        country: resolvedCountry,
        isValid,
        isPossible,
    };
}
/**
 * Parse une saisie lorsque l'indicatif international est deja porte par le
 * selecteur de pays. L'input reste ainsi au format national, meme lorsqu'un
 * numero E.164 est colle dans le champ.
 */
export function parseNationalPhoneInput(rawValue, selectedCountry) {
    const startsWithInternationalPrefix = rawValue.trimStart().startsWith('+');
    if (!startsWithInternationalPrefix) {
        const withoutCallingCodes = PHONE_COUNTRY_OPTIONS.reduce((value, { callingCode }) => value.split(callingCode).join(''), rawValue);
        return parsePhoneInput(withoutCallingCodes, selectedCountry);
    }
    const sanitized = sanitizePhoneInput(rawValue);
    const matchingOption = PHONE_COUNTRY_OPTIONS.find(({ callingCode }) => sanitized.startsWith(callingCode));
    // Un "+" seul (ou un indicatif non pris en charge) n'efface pas la saisie
    // existante : il est simplement retire du champ national.
    if (!matchingOption) {
        return parsePhoneInput(sanitized.slice(1), selectedCountry);
    }
    const parsed = parsePhoneNumberFromString(sanitized);
    const resolvedCountry = isSupportedPhoneCountry(parsed?.country)
        ? parsed.country
        : matchingOption.iso2;
    if (parsed) {
        return {
            displayValue: parsed.formatNational(),
            e164Value: parsed.isValid() ? parsed.number : null,
            country: resolvedCountry,
            isValid: parsed.isValid(),
            isPossible: parsed.isPossible(),
        };
    }
    const nationalDigits = sanitized.slice(matchingOption.callingCode.length);
    return parsePhoneInput(nationalDigits, resolvedCountry);
}
export function reformatPhoneForCountry(value, country) {
    /*
     * Si on reçoit déjà un E.164, il faut récupérer le national number
     * avant de changer de pays.
     */
    const parsedExisting = value.startsWith('+')
        ? parsePhoneNumberFromString(value)
        : undefined;
    const valueToFormat = parsedExisting?.nationalNumber ??
        value;
    return parsePhoneInput(valueToFormat, country);
}
/**
 * Pour charger un numéro provenant de l'API.
 */
export function formatStoredPhone(e164Value, fallbackCountry) {
    const parsed = parsePhoneNumberFromString(e164Value);
    if (!parsed) {
        return {
            displayValue: e164Value,
            e164Value: null,
            country: fallbackCountry,
            isValid: false,
            isPossible: false,
        };
    }
    const country = isSupportedPhoneCountry(parsed.country)
        ? parsed.country
        : fallbackCountry;
    return {
        displayValue: parsed.formatNational(),
        e164Value: parsed.isValid() ? parsed.number : null,
        country,
        isValid: parsed.isValid(),
        isPossible: parsed.isPossible(),
    };
}
export function parseInternationalPhone(rawValue, fallbackCountry) {
    const parsed = parsePhoneInput(rawValue, fallbackCountry);
    return {
        country: parsed.country,
        isValid: parsed.isValid,
        e164: parsed.e164Value,
    };
}
export function formatPhoneAsYouType(rawValue, country) {
    return parseNationalPhoneInput(rawValue, country).displayValue;
}
export function inferPhoneCountry(countryName) {
    const key = countryName
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{M}/gu, '');
    return COUNTRY_NAME_TO_ISO[key] ?? 'FR';
}
